// Contenido y navegación por teclado del menú "+" de la barra de entrada.
//
// El ORDEN de este array es el orden que se ve. Está acá, en un módulo puro y
// sin JSX, por dos motivos: se testea (que el orden y las etiquetas son los
// pedidos) y el componente queda siendo solo dibujo + eventos.
//
// Las dos entradas `proximamente` (Habilidades, Conectores) SÍ se enfocan con
// las flechas aunque no se puedan activar. Es a propósito y sigue la práctica
// de "disabled focusable" de WAI-ARIA: si se las salteara, quien navega con
// teclado o lector de pantalla nunca se enteraría de que existen — que es
// justamente lo que el menú quiere comunicar.

import type { InterruptoresChat } from '../tipos';

export type ItemMenuMas =
  | { tipo: 'accion'; id: 'adjuntar'; etiqueta: string; icono: 'clip' }
  | { tipo: 'separador'; id: string }
  | {
      tipo: 'proximamente';
      id: 'habilidades' | 'conectores';
      etiqueta: string;
      icono: 'chispa' | 'enchufe';
      ayuda: string;
    }
  | {
      tipo: 'toggle';
      id: keyof InterruptoresChat;
      etiqueta: string;
      icono: 'lupa-doc' | 'globo' | 'grafico';
      ayuda: string;
    };

export const ITEMS_MENU_MAS: ItemMenuMas[] = [
  {
    tipo: 'accion',
    id: 'adjuntar',
    etiqueta: 'Agregar archivos o fotos',
    icono: 'clip',
  },
  { tipo: 'separador', id: 'sep-1' },
  {
    tipo: 'proximamente',
    id: 'habilidades',
    etiqueta: 'Habilidades',
    icono: 'chispa',
    ayuda: 'Vas a poder enseñarle tareas propias a Atlas.',
  },
  {
    tipo: 'proximamente',
    id: 'conectores',
    etiqueta: 'Conectores',
    icono: 'enchufe',
    ayuda: 'Vas a poder enchufarle tus herramientas (Drive, Notion, Shopify…).',
  },
  { tipo: 'separador', id: 'sep-2' },
  {
    tipo: 'toggle',
    id: 'investigacion',
    etiqueta: 'Investigación',
    icono: 'lupa-doc',
    ayuda: 'Mira datos reales de Instagram, Google Maps y tiendas online.',
  },
  {
    tipo: 'toggle',
    id: 'busquedaWeb',
    etiqueta: 'Búsqueda web',
    icono: 'globo',
    ayuda: 'Busca en internet antes de responder.',
  },
  {
    tipo: 'toggle',
    id: 'respuestasGraficas',
    etiqueta: 'Respuestas gráficas',
    icono: 'grafico',
    ayuda: 'Puede contestar con un gráfico o una tabla cuando ayuda.',
  },
];

/** Rótulo de las entradas todavía no construidas. Uno solo, así las dos dicen
 *  exactamente lo mismo (dos redacciones distintas se leen como dos estados
 *  distintos). */
export const ROTULO_PROXIMAMENTE = 'próximamente';

/** ¿Se puede ACTIVAR con Enter/click? Los separadores no existen para el
 *  teclado y las entradas "próximamente" se enfocan pero no hacen nada. */
export const esActivable = (item: ItemMenuMas): boolean =>
  item.tipo === 'accion' || item.tipo === 'toggle';

/** ¿Recibe foco con las flechas? Todo menos los separadores. */
export const esEnfocable = (item: ItemMenuMas): boolean => item.tipo !== 'separador';

/** Los ids que recorre el foco, en orden. */
export const idsEnfocables = (items: ItemMenuMas[] = ITEMS_MENU_MAS): string[] =>
  items.filter(esEnfocable).map((i) => i.id);

/**
 * A qué id se mueve el foco desde `actual` corriéndose `delta` lugares.
 * Circular en los dos sentidos (el último baja al primero). Si `actual` no
 * está en la lista, se arranca por el extremo que corresponda al sentido.
 */
export function idFocoSiguiente(
  actual: string | null,
  delta: 1 | -1,
  items: ItemMenuMas[] = ITEMS_MENU_MAS,
): string | null {
  const ids = idsEnfocables(items);
  if (ids.length === 0) return null;
  const posicion = actual === null ? -1 : ids.indexOf(actual);
  if (posicion === -1) return delta === 1 ? ids[0] : ids[ids.length - 1];
  const siguiente = (posicion + delta + ids.length) % ids.length;
  return ids[siguiente];
}

/** Primer / último id enfocable (Home y End). */
export const idPrimerFoco = (items: ItemMenuMas[] = ITEMS_MENU_MAS): string | null =>
  idsEnfocables(items)[0] ?? null;

export const idUltimoFoco = (items: ItemMenuMas[] = ITEMS_MENU_MAS): string | null => {
  const ids = idsEnfocables(items);
  return ids[ids.length - 1] ?? null;
};
